import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  LinearProgress,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import API from "../../utils/Axios.utils";

const COLUMNS = [
  { id: "Vendor_Submission", label: "Vendor Submission", baseProbability: 10 },
  { id: "Internal_Screening", label: "Internal Screening", baseProbability: 15 },
  { id: "Internal_Evaluation", label: "Internal Evaluation", baseProbability: 20 },
  { id: "Internal_Feedback", label: "Internal Feedback", baseProbability: 25 },
  { id: "Submitted_To_Client", label: "Submitted to Client", baseProbability: 30 },
  { id: "Client_Evaluation", label: "Client Evaluation", baseProbability: 40 },
  { id: "Awaiting_for_slots", label: "Awaiting for slots", baseProbability: 50 },
  { id: "L1_Round", label: "L1 Round", baseProbability: 60 },
  { id: "L2_Round", label: "L2 Round", baseProbability: 70 },
  { id: "L3_Round", label: "L3 Round", baseProbability: 80 },
  { id: "Selected_Onboarding", label: "Pending Offer", baseProbability: 90 },
  { id: "Onboarding_In_Process", label: "Onboarding in process", baseProbability: 95 },
  { id: "Onboarded", label: "Onboarded", baseProbability: 100 },
  { id: "Rejected", label: "Rejected", baseProbability: 0 },
  { id: "Interview_Cancelled", label: "Interview Cancelled", baseProbability: 0 },
  { id: "Position_on_hold", label: "Position on Hold", baseProbability: 0 },
  { id: "No_Updates", label: "No Updates", baseProbability: 0 },
  { id: "Close-No_Client_Response", label: "Close-No Client Response", baseProbability: 0 },
];

const L_ROUNDS = ["L1_Round", "L2_Round", "L3_Round"];

const CANDIDATE_STATUS_COLOR = {
  Submitted: "info",
  Reviewed: "info",
  Short_listed: "primary",
  Interview_Scheduled: "primary",
  Staffed: "success",
  Rejected: "error",
  Interview_Cancelled: "error",
  Position_on_hold: "warning",
};

const statusColor = (status) => CANDIDATE_STATUS_COLOR[status] || "primary";

const isInterviewOverdue = (card, colId) =>
  L_ROUNDS.includes(colId) &&

  Boolean(card.interview_datetime) &&
  new Date(card.interview_datetime).getTime() < Date.now();

const KanbanCard = ({ card, onDragStart, colId, onClick, onDragOver }) => {
  const overdue = isInterviewOverdue(card, colId);
  return (
    <Paper
      elevation={1}
      draggable
      onDragStart={(e) => onDragStart(e, card.profile_id, colId)}
      onDragOver={onDragOver}
      onClick={() => onClick(card)}
      sx={{
        p: 1.5,
        mb: 1,
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
        borderRadius: 2,
        border: "1px solid #E2E8F0",
        "&:hover": { boxShadow: 3 },
        userSelect: "none",
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} noWrap>
        {card.candidate_name}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap display="block" mb={1}>
        {card.job_code ? `${card.job_code} - ` : ''}{card.job_title}
      </Typography>

      {colId === 'Onboarding_In_Process' && card.offer_status === 'Accepted' && (
        <Chip
          label="✅ Offer Accepted"
          size="small"
          color="success"
          sx={{ mb: 1, fontWeight: "bold" }}
        />
      )}

      {colId === 'Onboarding_In_Process' && card.offer_status === 'Declined' && (
        <Chip
          label="❌ Offer Declined"
          size="small"
          color="error"
          sx={{ mb: 1, fontWeight: "bold" }}
        />
      )}

      {colId === 'Selected_Onboarding' && (
        <Chip
          label={card.pre_onboarding_status === 'DOCUMENT_VERIFICATION_COMPLETED' ? "✅ Verified" : (card.pre_onboarding_status ? card.pre_onboarding_status.replace(/_/g, ' ') : "Pending Verification")}
          size="small"
          color={card.pre_onboarding_status === 'DOCUMENT_VERIFICATION_COMPLETED' ? "success" : "warning"}
          sx={{ mb: 1, fontWeight: "bold", fontSize: "0.7rem", height: 22 }}
        />
      )}

      {overdue && (
        <Chip
          label="Interview overdue"
          size="small"
          color="error"
          sx={{ fontSize: "0.65rem", height: 18, mb: 1 }}
        />
      )}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Chip label={card.supplier_name} size="small" sx={{ fontSize: "0.65rem", height: 20, maxWidth: 120 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0 }}>
          <LinearProgress
            variant="determinate"
            value={card.probability}
            color={statusColor(card.candidate_status)}
            sx={{ flex: 1, height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {card.probability}%
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const VisualPipelineKanban = () => {
  const [columns, setColumns] = useState(COLUMNS.map((c) => ({ ...c, cards: [] })));
  const [pendingChanges, setPendingChanges] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [selectedCard, setSelectedCard] = useState(null);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [unverifiedDialog, setUnverifiedDialog] = useState(null);
  const [stageWarningDialog, setStageWarningDialog] = useState(null);

  const fetchPipeline = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      const res = await API.get("api/interview/pipeline");
      const candidates = res?.data || [];

      setColumns(
        COLUMNS.map((col) => ({
          ...col,
          cards: candidates
            .filter((c) => c.detailed_status === col.id)
            .map((c) => ({ ...c, probability: col.baseProbability })),
        }))
      );
    } catch {
      setSnackbar({ open: true, message: "Failed to load pipeline data.", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPipeline(true);
  }, [fetchPipeline]);

  const handleDragStart = (e, cardId, sourceColId) => {
    e.dataTransfer.setData("cardId", String(cardId));
    e.dataTransfer.setData("sourceColId", sourceColId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    const container = document.getElementById("kanban-scroll-container");
    if (container) {
      const { left, right } = container.getBoundingClientRect();
      const edgeThreshold = 150;
      const scrollSpeed = 15;

      if (e.clientX < left + edgeThreshold) {
        container.scrollLeft -= scrollSpeed;
      } else if (e.clientX > right - edgeThreshold) {
        container.scrollLeft += scrollSpeed;
      }
    }
  };

  const handleDrop = (e, targetColId) => {
    e.preventDefault();
    const cardId = Number(e.dataTransfer.getData("cardId"));
    const sourceColId = e.dataTransfer.getData("sourceColId");
    if (sourceColId === targetColId) return;

    const sourceCol = columns.find((c) => c.id === sourceColId);
    const movedCard = sourceCol?.cards.find((c) => c.profile_id === cardId);
    
    // Check if moving from Accepted/Onboarded stage to another stage
    if (sourceColId === "Onboarded" || movedCard?.offer_status === "Accepted") {
      setStageWarningDialog({
        cardId,
        sourceColId,
        targetColId,
        candidateName: movedCard?.candidate_name || "Candidate",
        type: "ACCEPTED",
        title: "Move Accepted Candidate?",
        message: `This candidate (${movedCard?.candidate_name || "Candidate"}) has already accepted the offer and is in the Onboarded stage. Moving them to ${targetColId.replace(/_/g, " ")} will revert their accepted status. Are you sure you want to proceed?`,
        confirmText: "Yes, Move Candidate",
        cancelText: "No, Keep in Onboarded",
      });
      return;
    }

    // Check if moving from Rejected or Cancelled stage to an active stage
    if (["Rejected", "Interview_Cancelled", "Close-No_Client_Response"].includes(sourceColId)) {
      setStageWarningDialog({
        cardId,
        sourceColId,
        targetColId,
        candidateName: movedCard?.candidate_name || "Candidate",
        type: "REJECTED",
        title: `Re-open ${sourceColId.replace(/_/g, " ")} Candidate?`,
        message: `This candidate (${movedCard?.candidate_name || "Candidate"}) is currently marked as ${sourceColId.replace(/_/g, " ")}. Moving them to ${targetColId.replace(/_/g, " ")} will re-open their candidacy in the active pipeline. Are you sure you want to proceed?`,
        confirmText: "Yes, Re-open Candidacy",
        cancelText: `No, Keep ${sourceColId.replace(/_/g, " ")}`,
      });
      return;
    }

    // Check if moving to Offer Stage (Onboarding_In_Process) and documents are unverified
    if (targetColId === "Onboarding_In_Process") {
      const isDocVerified = movedCard?.pre_onboarding_status === "DOCUMENT_VERIFICATION_COMPLETED";
      if (!isDocVerified) {
        const statusLabel = movedCard?.pre_onboarding_status 
          ? movedCard.pre_onboarding_status.replace(/_/g, " ") 
          : "Pending Verification / Not Submitted";

        setUnverifiedDialog({
          cardId,
          sourceColId,
          targetColId,
          candidateName: movedCard?.candidate_name || "Candidate",
          statusLabel,
        });
        return;
      }
    }

    // Check if moving from a scheduled interview stage to any other stage for an upcoming (future) interview
    if (L_ROUNDS.includes(sourceColId)) {
      if (movedCard?.interview_datetime && movedCard?.interview_link) {
        const when = new Date(movedCard.interview_datetime);
        const isUpcoming = !isNaN(when.getTime()) && when.getTime() > Date.now();

        // Only prompt for cancellation if the scheduled interview time has not passed
        if (isUpcoming) {
          const whenLabel = when.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

          setCancelDialog({
            cardId,
            sourceColId,
            targetColId,
            whenLabel,
            stageLabel: sourceColId.replace(/_/g, " "),
          });
          return;
        }
      }
    }

    executeMove(cardId, sourceColId, targetColId, false);
  };

  const executeMove = (cardId, sourceColId, targetColId, cancelInterview) => {
    const sourceIdx = COLUMNS.findIndex((c) => c.id === sourceColId);
    const targetIdx = COLUMNS.findIndex((c) => c.id === targetColId);
    const direction = targetIdx > sourceIdx ? 1 : -1;
    const clearingSchedule = cancelInterview || (L_ROUNDS.includes(sourceColId) && sourceColId !== targetColId);

    setColumns((prev) => {
      const next = prev.map((col) => ({ ...col, cards: [...col.cards] }));
      const srcCol = next.find((c) => c.id === sourceColId);
      const tgtCol = next.find((c) => c.id === targetColId);
      if (!srcCol || !tgtCol) return prev;

      const cardIdx = srcCol.cards.findIndex((c) => c.profile_id === cardId);
      if (cardIdx === -1) return prev;

      const [movedCard] = srcCol.cards.splice(cardIdx, 1);
      const updatedCard = {
        ...movedCard,
        probability: Math.min(95, Math.max(5, movedCard.probability + direction * 20)),
        ...(clearingSchedule ? { interview_datetime: null, interview_link: null } : {}),
      };
      tgtCol.cards.push(updatedCard);
      return next;
    });

    setPendingChanges((prev) => {
      const nextMap = new Map(prev);
      const existing = nextMap.get(cardId) || {};
      nextMap.set(cardId, {
        ...existing,
        detailed_status: targetColId,
        cancelInterview: cancelInterview || existing.cancelInterview,
      });
      return nextMap;
    });
  };

  const handleSave = async () => {
    if (pendingChanges.size === 0) return;
    setSaving(true);
    try {
      const updates = Array.from(pendingChanges.entries()).map(([profile_id, data]) => ({
        profile_id,
        detailed_status: data.detailed_status,
        cancelInterview: data.cancelInterview || false,
      }));
      const res = await API.post("api/interview/pipeline/save", { updates });
      const responseData = res?.data?.data !== undefined ? res.data.data : (res?.data || {});
      const { updated = 0, errors = [] } = responseData;
      setPendingChanges(new Map());

      const hasErrors = errors && errors.length > 0;
      setSnackbar({
        open: true,
        message: hasErrors
          ? `Saved ${updated}.${errors[0]?.message ? ` ${errors[0].message}` : ""}`
          : `${updated} update(s) saved.`,
        severity: hasErrors ? "warning" : "success",
      });

      await fetchPipeline(false);
    } catch {
      setSnackbar({ open: true, message: "Failed to save changes.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box onDragOver={handleDragOver} sx={{ position: "relative", display: "flex", flexDirection: "column", height: "100%", minHeight: 0, overflow: "hidden" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Drag cards across columns to transition candidate interview stages.
          </Typography>
          {pendingChanges.size > 0 && (
            <Chip 
              label={`${pendingChanges.size} unsaved stage change(s)`} 
              size="small" 
              color="warning" 
              variant="outlined" 
              sx={{ fontWeight: 600 }} 
            />
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          disabled={pendingChanges.size === 0 || saving}
          onClick={handleSave}
          sx={{ textTransform: "none", fontWeight: 600, px: 2 }}
        >
          {saving ? "Saving…" : `Save Changes${pendingChanges.size > 0 ? ` (${pendingChanges.size})` : ""}`}
        </Button>
      </Box>

      <Box id="kanban-scroll-container" sx={{ display: "flex", gap: 1.5, overflowX: "auto", overflowY: "hidden", pb: 1, width: "100%", maxWidth: "100%", flexGrow: 1, minHeight: 0, alignItems: "stretch" }}>
        {columns.map((col) => (
          <Box
            key={col.id}
            onDrop={(e) => handleDrop(e, col.id)}
            onDragOver={handleDragOver}
            sx={{ minWidth: 220, flex: "0 0 220px", display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}
          >
            <Paper
              elevation={0}
              sx={{
                bgcolor: "#F8FAFC",
                border: "1px solid #E2E8F0",
                borderRadius: 2,
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 0,
                boxSizing: "border-box"
              }}
            >
              <Box 
                sx={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center", 
                  bgcolor: "#F8FAFC",
                  pt: 0.5,
                  pb: 1.5,
                  mb: 1,
                  flexShrink: 0
                }}
              >
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {col.label}
                </Typography>
                <Chip
                  label={col.cards.length}
                  size="small"
                  sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#E2E8F0" }}
                />
              </Box>

              <Box sx={{ flexGrow: 1, overflowY: "auto", minHeight: 0, pr: 0.5 }}>
                {col.cards.map((card) => (
                  <KanbanCard
                    key={card.profile_id}
                    card={card}
                    colId={col.id}
                    onDragStart={handleDragStart}
                    onClick={setSelectedCard}
                    onDragOver={handleDragOver}
                  />
                ))}
                {col.cards.length === 0 && (
                  <Box
                    sx={{
                      border: "2px dashed #CBD5E1",
                      borderRadius: 2,
                      p: 3,
                      textAlign: "center",
                      color: "#94A3B8",
                      fontSize: "0.8rem",
                    }}
                  >
                    Drop here
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        ))}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%", borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={Boolean(selectedCard)}
        onClose={() => setSelectedCard(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedCard && (
          <>
            <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
              {selectedCard.candidate_name}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 3 }}>
                <Chip
                  label={selectedCard.candidate_status}
                  color={statusColor(selectedCard.candidate_status)}
                  size="small"
                  sx={{ mr: 1, fontWeight: 600 }}
                />
                <Typography variant="body2" color="text.secondary" component="span">
                  {selectedCard.probability}% Probability
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Job Title
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedCard.job_title || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Job Code
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedCard.job_code || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Supplier
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedCard.supplier_name || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Interview Datetime
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedCard.interview_datetime
                      ? new Date(selectedCard.interview_datetime).toLocaleString("en-IN", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })
                      : "Not Scheduled"}
                  </Typography>
                </Grid>
              </Grid>

              {selectedCard.interview_link && (
                <Box sx={{ mt: 3, p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                    Interview Link
                  </Typography>
                  <Typography
                    variant="body2"
                    component="a"
                    href={selectedCard.interview_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "primary.main", wordBreak: "break-all" }}
                  >
                    {selectedCard.interview_link}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 0 }}>
              <Button onClick={() => setSelectedCard(null)} variant="outlined" sx={{ borderRadius: 8 }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={!!cancelDialog} onClose={() => setCancelDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: "error.main", fontWeight: 600 }}>Cancel Scheduled Interview?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This candidate has an upcoming {cancelDialog?.stageLabel} interview scheduled for:
          </Typography>
          <Typography variant="subtitle1" fontWeight={600} color="primary.main" sx={{ mb: 2 }}>
            {cancelDialog?.whenLabel}
          </Typography>
          <Alert severity="warning">
            Moving them to {cancelDialog?.targetColId?.replace(/_/g, " ")} will permanently cancel this interview and delete the meeting link. Are you sure you want to proceed?
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCancelDialog(null)} variant="outlined">
            No, Keep Interview
          </Button>
          <Button
            onClick={() => {
              if (cancelDialog) {
                executeMove(cancelDialog.cardId, cancelDialog.sourceColId, cancelDialog.targetColId, true);
                setCancelDialog(null);
              }
            }}
            variant="contained"
            color="error"
          >
            Yes, Cancel Interview
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unverified Candidate Warning Dialog */}
      <Dialog open={!!unverifiedDialog} onClose={() => setUnverifiedDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: "warning.dark", fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
          ⚠️ Candidate Documents Not Verified
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            <strong>{unverifiedDialog?.candidateName}</strong> has not completed pre-onboarding document verification.
          </Typography>
          <Box sx={{ mb: 2, p: 1.5, bgcolor: "#fffbe6", borderRadius: 2, border: "1px solid #ffe58f" }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Current Document Status:
            </Typography>
            <Typography variant="body2" fontWeight={600} color="warning.dark">
              {unverifiedDialog?.statusLabel}
            </Typography>
          </Box>
          <Alert severity="warning">
            Moving this candidate to <strong>Offer Stage</strong> before document verification is completed may bypass required pre-onboarding checks. Are you sure you want to proceed?
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setUnverifiedDialog(null)} variant="outlined">
            Cancel (Verify First)
          </Button>
          <Button 
            onClick={() => {
              if (unverifiedDialog) {
                executeMove(unverifiedDialog.cardId, unverifiedDialog.sourceColId, unverifiedDialog.targetColId, false);
                setUnverifiedDialog(null);
              }
            }} 
            variant="contained" 
            color="warning"
          >
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stage Change Confirmation Dialog for Accepted / Rejected */}
      <Dialog open={!!stageWarningDialog} onClose={() => setStageWarningDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: stageWarningDialog?.type === 'ACCEPTED' ? 'warning.dark' : 'error.main', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          ⚠️ {stageWarningDialog?.title}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            <strong>{stageWarningDialog?.candidateName}</strong>
          </Typography>
          <Alert severity={stageWarningDialog?.type === 'ACCEPTED' ? 'warning' : 'info'}>
            {stageWarningDialog?.message}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setStageWarningDialog(null)} variant="outlined">
            {stageWarningDialog?.cancelText || "Cancel"}
          </Button>
          <Button
            onClick={() => {
              if (stageWarningDialog) {
                executeMove(stageWarningDialog.cardId, stageWarningDialog.sourceColId, stageWarningDialog.targetColId, false);
                setStageWarningDialog(null);
              }
            }}
            variant="contained"
            color={stageWarningDialog?.type === 'ACCEPTED' ? 'warning' : 'primary'}
          >
            {stageWarningDialog?.confirmText || "Proceed"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Bottom Sticky Save Bar */}
      {pendingChanges.size > 0 && (
        <Paper
          elevation={6}
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            gap: 2.5,
            px: 3,
            py: 1.5,
            borderRadius: 8,
            bgcolor: "#1E293B",
            color: "white",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)",
            border: "1px solid #334155",
            animation: "fadeInUp 0.25s ease-out",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: "#F59E0B",
                boxShadow: "0 0 8px #F59E0B",
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              You have <strong>{pendingChanges.size}</strong> unsaved stage change{pendingChanges.size > 1 ? "s" : ""}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setPendingChanges(new Map());
                fetchPipeline();
              }}
              disabled={saving}
              sx={{
                color: "#94A3B8",
                borderColor: "#475569",
                borderRadius: 6,
                textTransform: "none",
                fontWeight: 500,
                "&:hover": { borderColor: "#94A3B8", bgcolor: "rgba(255,255,255,0.05)" },
              }}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                bgcolor: "#2563EB",
                "&:hover": { bgcolor: "#1D4ED8" },
                borderRadius: 6,
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
              }}
            >
              {saving ? "Saving Changes…" : "Save Changes"}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default VisualPipelineKanban;

